import { useReducedMotion } from 'motion/react';
import { useEffect, useState, type CSSProperties } from 'react';

import { PhoneFrame } from '../components/PhoneFrame';
import { WaitlistForm } from '../components/WaitlistForm';
import { useInView } from '../lib/useInView';
import { SessionScreen } from '../screens/SessionScreen';

/** Full attract-mode loop duration (typing + hint + btn + xp + pause). */
const ATTRACT_LOOP_MS = 6000;
const MOBILE_BP_PX = 769;

type ChipStyle = CSSProperties & { '--boot-i'?: number };

const chipStyle = (i: number): ChipStyle => ({ '--boot-i': i });

export const Hero = () => {
  const { ref: stageRef, inView } = useInView<HTMLDivElement>({
    threshold: 0.25,
    once: false,
    rootMargin: '0px',
  });
  const reducedMotion = useReducedMotion();
  const [loopKey, setLoopKey] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (reducedMotion) return;
    if (typeof window === 'undefined') return;
    if (window.innerWidth < MOBILE_BP_PX) return;
    const id = window.setInterval(() => setLoopKey((k) => k + 1), ATTRACT_LOOP_MS);
    return () => window.clearInterval(id);
  }, [inView, reducedMotion]);

  return (
    <section className="hero" aria-labelledby="hero-title">
      <div className="hero__bg" aria-hidden="true" />

      <div className="container hero__inner">
        <div>
          <div className="hero__hud" aria-hidden="true">
            <span className="hud__chip" style={chipStyle(0)}>
              <span style={{ color: 'var(--primary-strong)' }}>●</span> PRESS START
            </span>
            <span className="hud__chip hud__chip--yellow" style={chipStyle(1)}>
              NO CHEAT CODES
            </span>
          </div>

          <h1 id="hero-title" className="hero__title">
            Stop copying AI answers.
            <br />
            Start <em>building</em> real developer skills.
          </h1>

          <p className="hero__sub">
            You can finish tasks with AI. But can you explain the solution, debug without paste, or
            solve the same problem alone? Prepzio measures your real skill and coaches you toward
            independent thinking.
          </p>

          <div className="hero__form">
            <WaitlistForm
              source="hero"
              cta="Get my skill check"
              hint="No spam. Free beta invite when we open. Unsubscribe anytime."
            />
          </div>
        </div>

        <div className="hero__stage" aria-hidden="true" ref={stageRef}>
          <span className="hero__tile-deco hero__tile-deco--a">+12</span>
          <span className="hero__tile-deco hero__tile-deco--b">A→B</span>
          <span className="hero__tile-deco hero__tile-deco--c">XP</span>

          <PhoneFrame label="Prepzio practice session preview">
            <SessionScreen key={loopKey} attract />
          </PhoneFrame>
        </div>
      </div>
    </section>
  );
};
